// web/src/trading/DashboardController.tsx
//
// Slice 3 / Task F1: lifts the dashboard's per-pane state (left/right
// material ids, focused side, selected cluster id, expanded analysis id)
// out of TradingView so CopilotKit actions registered above the route
// outlet — e.g. `switch_material` — can mutate it from any route.
//
// Persistence: left/right material ids continue to use the
// trading.{left,right}_material_id localStorage keys via the existing
// useStoredState wrapper (cross-session UX preserved from the previous
// per-Pane ownership). focusedSide / selectedClusterId / expandedAnalysisId
// stay in-memory — they're transient interaction state that shouldn't
// outlive the tab.
//
// Spec §9.1 amendment A6 + Slice 2 TODO(slice-3): the dashboard_focus
// readable now publishes real selectedClusterId / expandedAnalysisId
// values instead of the placeholder nulls Slice 2 shipped.
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { PaneSide } from "./types";
import {
  DEFAULT_LEFT_MATERIAL_ID,
  DEFAULT_RIGHT_MATERIAL_ID,
  STORAGE_KEYS,
} from "./constants";
import { stringValue, useStoredState } from "./storage";

type Ctx = {
  leftMaterialId: string;
  rightMaterialId: string;
  setLeftMaterialId: (id: string) => void;
  setRightMaterialId: (id: string) => void;
  focusedSide: PaneSide;
  setFocusedSide: (side: PaneSide) => void;
  selectedClusterId: string | null;
  setSelectedClusterId: (id: string | null) => void;
  expandedAnalysisId: string | null;
  setExpandedAnalysisId: (id: string | null) => void;
};

const DashboardControllerCtx = createContext<Ctx | null>(null);

export function DashboardControllerProvider({
  children,
}: {
  children: ReactNode;
}) {
  // Materials persist across sessions via the same localStorage keys the
  // previous per-Pane state used — moving the wrappers up here preserves
  // the UX guarantee that a reload restores the user's last-selected
  // panes.
  const [leftMaterialId, setLeftMaterialId] = useStoredState(
    STORAGE_KEYS.leftMaterialId,
    DEFAULT_LEFT_MATERIAL_ID,
    stringValue(DEFAULT_LEFT_MATERIAL_ID),
  );
  const [rightMaterialId, setRightMaterialId] = useStoredState(
    STORAGE_KEYS.rightMaterialId,
    DEFAULT_RIGHT_MATERIAL_ID,
    stringValue(DEFAULT_RIGHT_MATERIAL_ID),
  );

  // Transient interaction state — intentionally not persisted.
  const [focusedSide, setFocusedSide] = useState<PaneSide>("left");
  const [selectedClusterId, setSelectedClusterIdState] = useState<
    string | null
  >(null);
  const [expandedAnalysisId, setExpandedAnalysisIdState] = useState<
    string | null
  >(null);

  const setSelectedClusterId = useCallback((id: string | null) => {
    setSelectedClusterIdState(id);
  }, []);

  const setExpandedAnalysisId = useCallback((id: string | null) => {
    setExpandedAnalysisIdState(id);
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      leftMaterialId,
      rightMaterialId,
      setLeftMaterialId,
      setRightMaterialId,
      focusedSide,
      setFocusedSide,
      selectedClusterId,
      setSelectedClusterId,
      expandedAnalysisId,
      setExpandedAnalysisId,
    }),
    [
      leftMaterialId,
      rightMaterialId,
      setLeftMaterialId,
      setRightMaterialId,
      focusedSide,
      selectedClusterId,
      setSelectedClusterId,
      expandedAnalysisId,
      setExpandedAnalysisId,
    ],
  );

  return (
    <DashboardControllerCtx.Provider value={value}>
      {children}
    </DashboardControllerCtx.Provider>
  );
}

export function useDashboardController(): Ctx {
  const v = useContext(DashboardControllerCtx);
  if (!v) {
    throw new Error(
      "useDashboardController used outside DashboardControllerProvider",
    );
  }
  return v;
}
